import React from 'react';
import { BoltIcon, DangerIcon, InfoIcon, WarningIcon } from '../icons';
import { Button, ButtonSize, ButtonType, IconName } from '@hills-design-system/components';
import { useTranslation } from '../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../types/constants';

export type BannerVariant = 'info' | 'danger' | 'missed' | 'warning';
export type BannerAutomation = 'off' | 'on';

/**
 * Banner component for displaying important messages, warnings, or information to users.
 *
 * There are two main ways to use the Banner:
 * 1. Use `message` prop for the main text and `subtitle` for supporting text
 * 2. Use `title` and `description` props (legacy approach)
 *
 * @example
 * // Basic usage with message and subtitle
 * <Banner
 *   variant="info"
 *   message="Important update available"
 *   subtitle="Please review and take action soon"
 *   actionLabel="Update now"
 *   onAction={() => handleUpdate()}
 * />
 *
 * @example
 * // With automation icon
 * <Banner
 *   variant="warning"
 *   automation="on"
 *   message="Automated process failed"
 *   actionLabel="Try again"
 *   onAction={() => retryProcess()}
 * />
 */
interface BannerProps {
  /** Visual style of the banner (affects colors and icons) */
  variant?: BannerVariant;
  /** Whether to show automation-related icons */
  automation?: BannerAutomation;
  /** Main title */
  title?: string;
  /** Secondary text that appears below the message */
  subtitle?: React.ReactNode;
  /** Additional CSS classes to apply to the banner */
  className?: string;
  /** Callback when the close button is clicked */
  onClose?: () => void;
  /** Callback when the action button is clicked */
  onAction?: () => void;
  /** Text for the action button */
  actionLabel?: string;
  /** Controls the responsive layout - 'sm' for mobile, 'lg' for desktop */
  breakpoint?: 'sm' | 'lg';
  size?: 'default' | 'compact';
  showBar?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  variant = 'info',
  automation = 'off',
  title,
  subtitle,
  className = '',
  onClose,
  onAction,
  actionLabel,
  breakpoint = 'lg',
  size = 'default',
  showBar = true,
}) => {
  const { t } = useTranslation();
  const variantStyles = {
    info: {
      bg: 'bg-banner-info-bg',
      border: 'border-banner-info-border',
      barColor: 'bg-banner-info-icon',
      iconColor: 'text-banner-info-icon',
    },
    danger: {
      bg: 'bg-banner-missed-bg',
      border: 'border-banner-missed-border',
      barColor: 'bg-banner-missed-icon',
      iconColor:
        automation === 'on' ? 'text-banner-danger-automationIcon' : 'text-banner-missed-icon',
    },
    missed: {
      bg: 'bg-banner-missed-bg',
      border: 'border-banner-missed-border',
      barColor: 'bg-banner-missed-icon',
      iconColor:
        automation === 'on' ? 'text-banner-missed-automationIcon' : 'text-banner-missed-icon',
    },
    warning: {
      bg: 'bg-banner-danger-bg',
      border: 'border-banner-danger-border',
      barColor: 'bg-banner-danger-icon',
      iconColor:
        automation === 'on' ? 'text-banner-danger-automationIcon' : 'text-banner-danger-icon',
    },
  };

  const styles = variantStyles[variant];

  const isSmallBreakpoint = breakpoint === 'sm';

  const renderIcon = () => {
    if (automation === 'on') {
      return <BoltIcon className={`${styles.iconColor}`} />;
    }

    switch (variant) {
      case 'info':
        return <InfoIcon className={`${styles.iconColor}`} />;
      case 'danger':
        return <DangerIcon className={`${styles.iconColor} w-6 h-6`} />;
      case 'missed':
      case 'warning':
        return <WarningIcon className={`${styles.iconColor} w-6 h-6`} />;
      default:
        return <InfoIcon className={`${styles.iconColor}`} />;
    }
  };

  return (
    <div className={`relative rounded-[8px] border ${styles.bg} ${styles.border} ${className}`}>
      {showBar && (
        <div
          className={`absolute top-0 left-0 h-full w-[7px] ${styles.barColor} rounded-l-[8px]`}
        />
      )}

      <div
        className={`flex ${
          isSmallBreakpoint
            ? 'flex-col gap-4 p-5 pl-4'
            : `items-center justify-between ${size === 'compact' ? 'p-4 pl-5' : 'p-8 pl-8'}`
        }`}
      >
        <div
          className={`flex ${isSmallBreakpoint ? 'flex-col gap-2 w-full' : 'items-center gap-3 flex-1'}`}
        >
          <div className="flex-shrink-0">{renderIcon()}</div>

          <p className="text-base text-black-600 font-medium">
            {title && <span className="font-bold text-black-800">{title}</span>}
            {title && subtitle && <span className="font-bold text-black-800"> — </span>}
            {subtitle}
          </p>
        </div>

        {onAction && actionLabel && (
          <div className={`flex items-center ${isSmallBreakpoint ? 'mt-2' : 'ml-4'}`}>
            <Button
              label={actionLabel}
              type={ButtonType.Tertiary}
              size={ButtonSize.Small}
              icon={IconName.ChevronRight}
              align="left"
              onClick={onAction}
              className="!text-primary hover:!text-primary-dark !font-bold hover:bg-transparent"
            />
          </div>
        )}

        {onClose && (
          <button
            type="button"
            className={`text-gray-400 hover:text-gray-500 ${
              isSmallBreakpoint ? 'absolute top-3 right-3' : 'ml-4'
            }`}
            onClick={onClose}
          >
            <span className="sr-only">{t(TranslationKeyEnum.Close)}</span>
            <svg
              className="h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Banner;
